import { Edit, X } from "lucide-react";
import React, { useEffect, useState } from "react";

import { getAllCategories } from "../../services/category.service";
import { updateExpositorInfo } from "../../services/expositor.service";
import {
  normalizeInstagram,
  syncUserToKommo,
} from "../../services/kommo.service";
import { parseDateHifen } from "../../utils/parseDate";
import { validateCpf } from "../../utils/validateCpf";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../ui-components/ToastProvider";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: any;
  onUserDataUpdate: (newData: any) => void;
}

export default function ProfileModal({
  isOpen,
  onClose,
  userData,
  onUserDataUpdate,
}: ProfileModalProps) {
  const { showToast } = useToast();
  const [inEditMode, setEditMode] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    async function fetchCategories() {
      try {
        const categoriesData = await getAllCategories();
        setCategories(categoriesData || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    }
    fetchCategories();
  }, []);

  useEffect(() => {
    if (userData?.category_id) {
      setSelectedCategory(userData.category_id.toString());
    }
  }, [userData]);

  const handleDataSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const items = Object.fromEntries(formData.entries());

    const cpf = (items as any).cpf as string;
    if (cpf && cpf.trim() && !validateCpf(cpf)) {
      showToast("CPF inválido. Verifique os dígitos.", { severity: "error" });
      return;
    }

    const normalizedInstagram = normalizeInstagram((items as any).instagram_url || "");
    (items as any).instagram_url = normalizedInstagram;

    updateExpositorInfo(items).then((response) => {
      if (response.status === 200) {
        syncUserToKommo((userData as any).id, {
          name: (items as any).name,
          instagram_url: normalizedInstagram,
          brand_name: (items as any).brand_name,
          brand_description: (items as any).brand_description,
          brand_items: (items as any).brand_items,
          cpf: (items as any).cpf,
          cnpj: (items as any).cnpj,
          city: (items as any).city,
          state: (items as any).state,
          phone: (items as any).phone,
        }).catch(() => {});
        window.location.reload();
      } else {
        showToast(
          response.message || "Ocorreu algum erro ao alterar seus dados, tente novamente!",
          { severity: "error" }
        );
        onClose();
      }
    });
  };

  if (!isOpen) return null;

  return (
<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-2 sm:p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <CardTitle className="text-gray-900">
            Seus Dados Cadastrados
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditMode(!inEditMode)}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              {inEditMode ? "Editar" : "Cancelar"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
<CardContent className="p-4 md:p-6">
          <form onSubmit={handleDataSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700">
                  Nome
                </Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={userData.name}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">
                  E-mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  defaultValue={userData.email}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthday" className="text-gray-700">
                  Data de Nascimento
                </Label>
                <Input
                  id="birthday"
                  name="birthday"
                  type="date"
                  defaultValue={parseDateHifen(userData.birthday)}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city" className="text-gray-700">
                  Cidade
                </Label>
                <Input
                  id="city"
                  name="city"
                  defaultValue={userData.city}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state" className="text-gray-700">
                  Estado
                </Label>
                <Input
                  id="state"
                  name="state"
                  defaultValue={userData.state}
                  disabled={inEditMode}
                  maxLength={2}
                  minLength={2}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zipcode" className="text-gray-700">
                  CEP
                </Label>
                <Input
                  id="zipcode"
                  name="zipcode"
                  defaultValue={userData.zipcode}
                  disabled={inEditMode}
                  maxLength={8}
                  minLength={8}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="district" className="text-gray-700">
                  Bairro
                </Label>
                <Input
                  id="district"
                  name="district"
                  defaultValue={userData.district}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-gray-700">
                  Rua
                </Label>
                <Input
                  id="address"
                  name="address"
                  defaultValue={userData.address}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_number" className="text-gray-700">
                  Número
                </Label>
                <Input
                  id="address_number"
                  name="address_number"
                  type="number"
                  defaultValue={userData.address_number || ""}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address_complement" className="text-gray-700">
                  Complemento
                </Label>
                <Input
                  id="address_complement"
                  name="address_complement"
                  defaultValue={userData.address_complement || ""}
                  disabled={inEditMode}
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700">
                  Telefone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  defaultValue={userData.phone}
                  disabled={inEditMode}
                  required
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf" className="text-gray-700">
                  CPF
                </Label>
                <Input
                  id="cpf"
                  name="cpf"
                  defaultValue={userData.cpf || ""}
                  disabled={inEditMode}
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="cnpj" className="text-gray-700">
                  CNPJ
                </Label>
                <Input
                  id="cnpj"
                  name="cnpj"
                  defaultValue={userData.cnpj || ""}
                  disabled={inEditMode}
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">
                Informações da Marca
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="brand_name" className="text-gray-700">
                    Nome da Marca
                  </Label>
                  <Input
                    id="brand_name"
                    name="brand_name"
                    defaultValue={userData.brand_name}
                    disabled={inEditMode}
                    required
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="instagram_url" className="text-gray-700">
                    Instagram
                  </Label>
                  <Input
                    id="instagram_url"
                    name="instagram_url"
                    defaultValue={userData.instagram_url || ""}
                    disabled={inEditMode}
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="brand_description" className="text-gray-700">
                    Descrição da Marca
                  </Label>
                  <Textarea
                    id="brand_description"
                    name="brand_description"
                    defaultValue={userData.brand_description || ""}
                    disabled={inEditMode}
                    rows={4}
                    placeholder="Descreva sua marca aqui"
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="brand_items" className="text-gray-700">
                    Itens da sua marca
                  </Label>
                  <Textarea
                    id="brand_items"
                    name="brand_items"
                    defaultValue={userData.brand_items || ""}
                    disabled={inEditMode}
                    rows={3}
                    placeholder="Descreva os itens que sua marca possui"
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="category_id" className="text-gray-700">
                    Categoria da Marca
                  </Label>
                  <Select
                    name="category_id"
                    value={selectedCategory}
                    onValueChange={setSelectedCategory}
                    disabled={inEditMode}
                    required
                  >
                    <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      {categories.length > 0 ? (
                        categories
                          .filter((x) => x.active)
                          .map((x) => (
                            <SelectItem key={x.id} value={x.id.toString()}>
                              {x.name}
                            </SelectItem>
                          ))
                      ) : (
                        <SelectItem value="" disabled>
                          Carregando categorias...
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {!inEditMode && (
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="px-4 py-2 bg-[#24324D] text-white rounded-md hover:bg-[#1a2538] transition-colors"
                >
                  Salvar Alterações
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}